import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Receipt, Printer, MessageCircle, Trash2, Shirt } from "lucide-react";
import PickerWithAdd, { createVendor } from "@/components/PickerWithAdd";
import WhatsAppButton from "@/components/WhatsAppButton";
import TShirtDiagram, { totalPrintCost } from "@/components/TShirtDiagram";
import { AttachmentLink } from "@/components/AttachmentLink";
import { useAuth } from "@/auth/AuthContext";

const STAGES = ["procurement","material_received","qc1","customization","qc2","packing","dispatched","delivered"];
const STAGE_LABEL = {
  procurement: "Procurement", material_received: "Material In", qc1: "QC 1",
  customization: "Customization", qc2: "QC 2", packing: "Packing",
  dispatched: "Dispatched", delivered: "Delivered",
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "production", label: "Production" },
  { key: "dispatch", label: "Dispatch" },
  { key: "expenses", label: "Expenses & Profit" },
  { key: "attachments", label: "Files" },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [o, setO] = useState(null);
  const [tab, setTab] = useState("overview");
  const [vendors, setVendors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setO(data);
    const v = await api.get("/vendors"); setVendors(v.data);
    const ex = await api.get("/expenses"); setExpenses(ex.data.filter(x => x.order_id === id));
    const at = await api.get(`/attachments?order_id=${id}`); setAttachments(at.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!o) return <div className="p-8 text-[#757169]">Loading order…</div>;

  const idx = STAGES.indexOf(o.stage);
  const revenue = Number(o.total || 0);
  const spent = expenses.reduce((a,b)=>a+Number(b.amount||0), 0);
  const profit = revenue - spent;

  const advance = async (stage) => {
    try { await api.post(`/orders/${id}/stage`, { stage }); load(); toast.success(`Moved to ${STAGE_LABEL[stage]}`); }
    catch(e){ toast.error(e.response?.data?.detail || "Blocked"); }
  };

  const generateInvoice = async () => {
    try { const { data } = await api.post(`/invoices/from-order/${id}`); toast.success(`Invoice ${data.invoice_no} created`); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    const confirm = window.prompt(`Type the order number "${o.order_no}" to permanently delete this order AND all related payments, invoices, expenses, and attachments.`);
    if (confirm !== o.order_no) { toast.error("Cancelled — order number didn't match"); return; }
    try { await api.delete(`/orders/${id}`); toast.success("Order deleted"); nav("/orders"); }
    catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };

  const readyForDispatchWa = () => {
    const url = `${window.location.origin}/order-form/${o.order_form_token}`;
    const balance = Number(o.balance_amount ?? (o.total - (o.advance_amount||0)));
    return `Hi ${o.customer_name}, your order ${o.order_no} is ready for dispatch! 🎉 Please pay the balance of ₹${balance.toLocaleString("en-IN")} and collect your goods. Order form: ${url}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 no-print">
        <Link to="/orders" className="btn-ghost text-xs flex items-center gap-1"><ArrowLeft size={12}/> Orders</Link>
      </div>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny mb-1">Order</div>
          <h1 className="font-display text-3xl tracking-tight mono">{o.order_no}</h1>
          <div className="text-[#757169] mt-1">
            {o.customer_name}
            {o.customer_phone && <> · <span className="mono">{o.customer_phone}</span></>}
            {" · "}
            <span className="mono">{inr(o.total)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          {o.customer_phone && (
            <WhatsAppButton phone={o.customer_phone}
              message={`Hi ${o.customer_name}, an update on your order ${o.order_no} from Marsh Clothing.`}
              label="WhatsApp customer" size="lg" testid="wa-customer" />
          )}
          {o.artwork_status && o.artwork_status !== "not_required" && o.customer_phone && (
            <WhatsAppButton phone={o.customer_phone}
              message={`Hi ${o.customer_name}, please review the artwork / mockup for your order ${o.order_no} and approve here:\n${window.location.origin}/mockup/${o.artwork_token}`}
              label="Send artwork for approval" size="lg" testid="wa-artwork-approval" />
          )}
          {["packing","qc2","ready"].includes(o.stage) && o.customer_phone && (
            <WhatsAppButton phone={o.customer_phone} message={readyForDispatchWa()}
              label="Send 'Ready for pickup' WhatsApp" size="lg" testid="wa-ready-dispatch"/>
          )}
          <a href={`/print/order/${o.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-sm flex items-center gap-1"><Printer size={14}/> Order Form</a>
          <button className="btn-primary flex items-center gap-2" onClick={generateInvoice} data-testid="generate-invoice-btn">
            <Receipt size={16}/> Generate invoice
          </button>
          {isAdmin && (
            <button onClick={doDelete} className="btn-ghost text-sm flex items-center gap-1 text-[#B83A3A]" data-testid="order-delete"><Trash2 size={14}/> Delete</button>
          )}
        </div>
      </div>

      {/* Stage tracker */}
      <div className="card-surface p-5">
        <div className="label-tiny mb-3">Production stage</div>
        <div className="stage-track">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              {i>0 && <div className="stage-connector" />}
              <button onClick={()=>advance(s)}
                className={`stage-node ${i<idx?"done":i===idx?"current":""}`}
                data-testid={`stage-${s}`}>
                {i<idx && <CheckCircle2 size={12}/>}
                {STAGE_LABEL[s]}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#EAE5DB] flex gap-1 no-print overflow-x-auto whitespace-nowrap">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 ${
              tab===t.key ? "border-[#C45A41] text-[#C45A41]" : "border-transparent text-[#757169] hover:text-[#2C2B29]"
            }`} style={{fontFamily:"Manrope, sans-serif"}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab o={o} reload={load} />}
      {tab === "production" && <ProductionTab o={o} vendors={vendors} reload={load} />}
      {tab === "dispatch" && <DispatchTab o={o} reload={load} />}
      {tab === "expenses" && isAdmin && <ExpensesTab o={o} expenses={expenses} revenue={revenue} spent={spent} profit={profit} reload={load} />}
      {tab === "expenses" && !isAdmin && <div className="card-surface p-8 text-center text-[#757169]">Profit & expenses visible to Admin only.</div>}
      {tab === "attachments" && <AttachmentsTab orderId={id} attachments={attachments} reload={load} order={o} />}
    </div>
  );
}


// ---------------- Artwork approval card ----------------
function ArtworkCard({ o, reload }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(o.artwork_notes || "");
  const [previewUrl, setPreviewUrl] = useState(o.artwork_url || "");
  const [fileName, setFileName] = useState("");
  const publicUrl = `${window.location.origin}/mockup/${o.artwork_token}`;
  const statusMap = {
    not_required: { label: "Not required", cls: "badge-neutral" },
    pending:      { label: "Awaiting approval", cls: "badge-warn" },
    approved:     { label: "Approved", cls: "badge-success" },
    changes_requested: { label: "Changes requested", cls: "badge-danger" },
  };
  const st = statusMap[o.artwork_status] || statusMap.not_required;
  const isActive = o.artwork_status && o.artwork_status !== "not_required";

  const [extraPreviews, setExtraPreviews] = useState([]);

  const handleFile = async (fileList) => {
    // Support both single-file and multi-file selection.
    const files = fileList ? (Array.isArray(fileList) ? fileList : Array.from(fileList)) : [];
    if (!files.length) return;
    if (files.some(f => f.size > 5 * 1024 * 1024)) {
      toast.error("Each mockup must be ≤ 5 MB — please compress the image.");
      return;
    }
    const readers = files.map(f => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res({ url: r.result, name: f.name });
      r.onerror = rej;
      r.readAsDataURL(f);
    }));
    try {
      const results = await Promise.all(readers);
      const [first, ...rest] = results;
      setPreviewUrl(first.url);
      setFileName(first.name);
      setExtraPreviews(rest);
    } catch { toast.error("Could not read files"); }
  };

  const requestApproval = async () => {
    if (!previewUrl) { toast.error("Upload a mockup image first"); return; }
    setBusy(true);
    try {
      const extraUrls = extraPreviews.map(p => p.url);
      await api.put(`/orders/${o.id}`, {
        artwork_status: "pending",
        artwork_url: previewUrl,
        artwork_urls: extraUrls,
        artwork_notes: notes,
      });
      // Archive every mockup as an attachment for record keeping (best-effort)
      const allShots = [{ url: previewUrl, name: fileName || `mockup-${Date.now()}.png` }, ...extraPreviews];
      for (const s of allShots) {
        try {
          await api.post(`/orders/${o.id}/final-photo`, { view: "mockup", file_url: s.url, file_name: s.name || `mockup-${Date.now()}.png` });
        } catch (_) { /* best-effort */ }
      }
      const count = 1 + extraPreviews.length;
      toast.success(`Approval link ready with ${count} mockup${count > 1 ? "s" : ""} — share it with the customer`);
      setExtraPreviews([]);
      reload();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const uploadNewVersion = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB — please compress the image."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await api.put(`/orders/${o.id}`, { artwork_url: reader.result, artwork_status: "pending" });
        try {
          await api.post(`/orders/${o.id}/final-photo`, { view: "mockup", file_url: reader.result, file_name: file.name || `mockup-${Date.now()}.png` });
        } catch (_) { /* best-effort */ }
        toast.success("New mockup uploaded — customer can approve"); reload();
      } catch { toast.error("Failed"); } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const markNotRequired = async () => {
    if (!window.confirm("Turn off artwork approval for this order?")) return;
    try { await api.put(`/orders/${o.id}`, { artwork_status: "not_required" }); reload(); }
    catch { toast.error("Failed"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the approval link? The old link will stop working.")) return;
    try { await api.post(`/orders/${o.id}/regenerate-artwork-token`); toast.success("New link generated"); reload(); }
    catch { toast.error("Failed"); }
  };

  const waMsg = `Hi ${o.customer_name}, please review the artwork / mockup for your Marsh Clothing order ${o.order_no} and approve here:\n${publicUrl}\n\nLet me know if any tweaks are needed 🙏`;

  return (
    <div className="card-surface p-5" data-testid="artwork-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tiny flex items-center gap-1"><Shirt size={11}/> Artwork / Mockup approval</div>
        <span className={`badge ${st.cls}`}>{st.label}</span>
      </div>

      {!isActive ? (
        <>
          <p className="text-xs text-[#75706A] mb-3">Upload one or more mockup images so the customer can review & approve before we cut fabric.</p>
          <div className="space-y-3">
            <div>
              <div className="label-tiny mb-1">Mockup file(s) <span className="normal-case text-[10px]">(pick multiple — first is the main image)</span></div>
              <input
                type="file"
                accept="image/*"
                multiple
                className="input-field text-xs file:mr-2 file:rounded file:border-0 file:bg-[#242321] file:text-white file:px-2 file:py-1"
                onChange={e => handleFile(e.target.files)}
                data-testid="artwork-file-input"
              />
              {previewUrl && (
                <div className="mt-2 rounded border border-[#EAE5DB] overflow-hidden bg-[#FBF8F1]">
                  <img src={previewUrl} alt="mockup preview" className="w-full max-h-32 object-contain"/>
                  <div className="text-[10px] text-[#75706A] text-center py-1 border-t border-[#EAE5DB]">Main mockup</div>
                </div>
              )}
              {extraPreviews.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  {extraPreviews.map((p, i) => (
                    <div key={i} className="relative rounded border border-[#EAE5DB] overflow-hidden bg-[#FBF8F1]">
                      <img src={p.url} alt={p.name} className="w-full h-16 object-cover"/>
                      <button
                        type="button"
                        className="absolute top-0.5 right-0.5 bg-white/90 rounded-full w-4 h-4 text-[10px] leading-none text-[#B83A3A] hover:bg-white"
                        onClick={() => setExtraPreviews(list => list.filter((_, idx) => idx !== i))}
                        title="Remove"
                        data-testid={`artwork-extra-remove-${i}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {fileName && <div className="text-[10px] text-[#75706A] mt-1">{extraPreviews.length > 0 ? `${1 + extraPreviews.length} mockups ready` : fileName}</div>}
            </div>
            <div>
              <div className="label-tiny mb-1">Notes to customer</div>
              <textarea rows={2} className="input-field text-xs" placeholder="What should the customer confirm?" value={notes} onChange={e=>setNotes(e.target.value)} data-testid="artwork-notes-input"/>
            </div>
            <button disabled={busy || !previewUrl} onClick={requestApproval} className="btn-primary w-full text-xs disabled:opacity-50" data-testid="artwork-request-btn">
              Send for customer approval
            </button>
          </div>
        </>
      ) : (
        <>
          {o.artwork_url && (
            <AttachmentLink url={o.artwork_url} name="mockup.png" className="block mb-2">
              <img src={o.artwork_url} alt="mockup" className="w-full max-h-40 object-contain rounded border border-[#EAE5DB]"/>
            </AttachmentLink>
          )}
          {(o.artwork_urls || []).length > 0 && (
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {o.artwork_urls.map((u, i) => (
                <AttachmentLink key={i} url={u} name={`mockup-${i+1}.png`}>
                  <img src={u} alt={`mockup ${i+1}`} className="w-full h-16 object-cover rounded border border-[#EAE5DB]"/>
                </AttachmentLink>
              ))}
            </div>
          )}
          {o.artwork_notes && <div className="text-xs text-[#75706A] mb-2">&ldquo;{o.artwork_notes}&rdquo;</div>}
          <div className="text-[10px] text-[#75706A] mb-1">Public approval link:</div>
          <div className="flex gap-1 mb-2">
            <input readOnly className="input-field text-[10px] font-mono flex-1" value={publicUrl}/>
            <button className="btn-ghost text-xs px-2" onClick={()=>{navigator.clipboard?.writeText(publicUrl); toast.success("Copied");}}>Copy</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2 items-center">
            {o.customer_phone && (
              <WhatsAppButton phone={o.customer_phone} message={waMsg} label="Send link" size="sm" testid="artwork-wa-btn"/>
            )}
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Preview</a>
            <AttachmentLink url={o.artwork_url} name="mockup.png" className="btn-ghost text-xs">Open file</AttachmentLink>
            <label className="btn-ghost text-xs cursor-pointer">
              Upload new (replace main)
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadNewVersion(e.target.files?.[0])} data-testid="artwork-reupload"/>
            </label>
            <label className="btn-ghost text-xs cursor-pointer" data-testid="artwork-add-more-label">
              + Add more mockups
              <input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                const readAll = files.map(f => new Promise((res, rej) => {
                  if (f.size > 5 * 1024 * 1024) { rej(new Error("Max 5 MB per image")); return; }
                  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f);
                }));
                try {
                  const urls = await Promise.all(readAll);
                  const nextUrls = [...(o.artwork_urls || []), ...urls];
                  await api.put(`/orders/${o.id}`, { artwork_urls: nextUrls, artwork_status: o.artwork_status === "not_required" ? "pending" : o.artwork_status });
                  for (const u of urls) {
                    try { await api.post(`/orders/${o.id}/final-photo`, { view: "mockup", file_url: u, file_name: `mockup-${Date.now()}.png` }); } catch (_){ /* non-fatal */ }
                  }
                  toast.success(`${urls.length} mockup(s) added`);
                  reload();
                } catch (err) { toast.error(err.message || "Upload failed"); }
                e.target.value = "";
              }} data-testid="artwork-add-more"/>
            </label>
            <button onClick={regenerate} className="btn-ghost text-xs" data-testid="artwork-regen-btn">New link</button>
            <button onClick={markNotRequired} className="btn-ghost text-xs text-[#B83A3A]" data-testid="artwork-off-btn">Turn off</button>
          </div>
        </>
      )}
    </div>
  );
}


// ---------------- Overview ----------------
const SIZES_LIST = ["XS","S","M","L","XL","2XL","3XL","4XL"];
const OVERSIZE_KEYS = ["3XL","4XL","5XL"];

function OverviewTab({ o, reload }) {
  const [adv, setAdv] = useState(o.advance_amount || 0);
  const [exp, setExp] = useState(o.expected_delivery_at?.slice(0,10) || "");
  const [openTee, setOpenTee] = useState(-1);
  const [teeDraft, setTeeDraft] = useState({}); // idx -> print_locations
  const [editItems, setEditItems] = useState(false);
  const [itemsDraft, setItemsDraft] = useState(null);
  const total = Number(o.total || 0);
  const balance = total - Number(adv || 0);

  const startEditItems = () => {
    setItemsDraft(o.items.map(it => ({ ...it, sizes: { ...(it.sizes || {}) } })));
    setEditItems(true);
  };
  const setDraftField = (i, k, v) => setItemsDraft(items => items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const setDraftSize = (i, sz, raw) => setItemsDraft(items => items.map((it, idx) => {
    if (idx !== i) return it;
    const sizes = { ...(it.sizes || {}) };
    const v = raw === "" ? 0 : Number(raw) || 0;
    if (!v) delete sizes[sz]; else sizes[sz] = v;
    const total = Object.values(sizes).reduce((a, b) => a + Number(b || 0), 0);
    return { ...it, sizes, qty: total || it.qty };
  }));
  const saveEditedItems = async () => {
    try {
      await api.put(`/orders/${o.id}`, { items: itemsDraft });
      toast.success("Order items updated");
      setEditItems(false);
      setItemsDraft(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Normalise legacy scalar/boolean print_locations values into the {method, colors} shape.
  const normalisePL = (raw) => {
    const out = {};
    Object.entries(raw || {}).forEach(([k, v]) => {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        out[k] = { ...v };
      } else if (typeof v === "number") {
        // Old shape stored just a qty; convert to a screen-1-color default.
        out[k] = { method: "screen", colors: 1 };
      } else if (v === true) {
        out[k] = { method: "screen", colors: 1 };
      }
    });
    return out;
  };

  const openZoneEditor = (idx) => {
    setOpenTee(idx);
    setTeeDraft((d) => ({ ...d, [idx]: normalisePL(o.items[idx].print_locations) }));
  };

  const savePayments = async () => {
    try {
      await api.put(`/orders/${o.id}`, { advance_amount: Number(adv), balance_amount: balance, expected_delivery_at: exp || null });
      toast.success("Order updated"); reload();
    } catch(e) { toast.error("Failed"); }
  };

  const saveZones = async (idx) => {
    const draft = teeDraft[idx] ?? normalisePL(o.items[idx].print_locations);
    const newPrintCost = totalPrintCost(draft).total;
    // Store customization cost per piece separately so it stays visible + editable and adds to totals.
    const newItems = o.items.map((x, i) => i === idx
      ? { ...x, print_locations: draft, customization_price: Math.round(newPrintCost * 100) / 100 }
      : x);
    try {
      await api.put(`/orders/${o.id}`, { items: newItems });
      toast.success(`Print zones updated — customization ₹${newPrintCost.toFixed(0)}/pc added to totals`);
      setOpenTee(-1);
      reload();
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-tiny">Line items</div>
          {!editItems ? (
            <button className="btn-ghost text-xs" onClick={()=>startEditItems()} data-testid="edit-items-start">Edit items</button>
          ) : (
            <div className="flex gap-1.5">
              <button className="btn-ghost text-xs" onClick={()=>{ setEditItems(false); setItemsDraft(null); }} data-testid="edit-items-cancel">Cancel</button>
              <button className="btn-primary text-xs" onClick={saveEditedItems} data-testid="edit-items-save">Save changes</button>
            </div>
          )}
        </div>
        {!editItems ? (
        <table className="data-table">
          <thead><tr><th>Product</th><th>Color</th><th>Customization</th><th>GST%</th><th>Qty</th><th>Unit</th><th>Cust/pc</th><th>3XL+</th><th>Total</th><th>Print zones</th></tr></thead>
          <tbody>
            {o.items.map((it, i) => {
              const cust = Number(it.customization_price || 0);
              const over = Number(it.oversize_surcharge || 0);
              const overCount = OVERSIZE_KEYS.reduce((a,k)=>a+Number((it.sizes||{})[k]||0),0);
              const line = Number(it.qty) * (Number(it.unit_price) + cust) + over * overCount;
              return (
              <tr key={it.id}>
                <td className="font-medium">{it.product}</td>
                <td className="text-[#757169]">{it.color||"—"}</td>
                <td className="text-[#757169]">{it.customization||"—"}</td>
                <td className="mono">{it.gst_percent != null ? `${it.gst_percent}%` : "—"}</td>
                <td className="mono">{it.qty}</td>
                <td className="mono">{inr(it.unit_price)}</td>
                <td className="mono" style={{color: cust>0?"#C45A41":"#B3AC9E"}}>{cust>0?inr(cust):"—"}</td>
                <td className="mono text-xs" title={overCount ? `${overCount} pcs × ${inr(over)}` : ""}>{over>0 && overCount>0 ? `+${inr(over*overCount)}` : "—"}</td>
                <td className="mono">{inr(line)}</td>
                <td>
                  <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => (openTee === i ? setOpenTee(-1) : openZoneEditor(i))} data-testid={`overview-tee-${i}`}>
                    <Shirt size={11}/>
                    {Object.keys(it.print_locations || {}).length > 0
                      ? `${Object.keys(it.print_locations).length} zone${Object.keys(it.print_locations).length>1?"s":""}`
                      : "Set"}
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        ) : (
          <div className="space-y-4" data-testid="items-editor">
            {itemsDraft.map((it, i) => (
              <div key={it.id} className="border border-[#EAE5DB] rounded-md p-3 bg-[#FBF8F1]">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sm">{it.product}{it.color ? ` · ${it.color}` : ""}</div>
                  {itemsDraft.length > 1 && (
                    <button type="button" className="text-[#B83A3A] text-xs hover:bg-[#F7DFDF] px-2 py-1 rounded"
                      onClick={()=>setItemsDraft(items => items.filter((_, idx) => idx !== i))}
                      data-testid={`edit-remove-${i}`}>Remove line</button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
                  <div className="col-span-2 md:col-span-3"><div className="label-tiny mb-1">Product</div>
                    <input className="input-field" value={it.product||""} onChange={e=>setDraftField(i,"product",e.target.value)} data-testid={`edit-product-${i}`}/></div>
                  <div className="col-span-2 md:col-span-2"><div className="label-tiny mb-1">Color</div>
                    <input className="input-field" value={it.color||""} onChange={e=>setDraftField(i,"color",e.target.value)}/></div>
                  <div className="col-span-2 md:col-span-3"><div className="label-tiny mb-1">Customization type</div>
                    <select className="input-field" value={it.customization||""} onChange={e=>setDraftField(i,"customization",e.target.value)} data-testid={`edit-custtype-${i}`}>
                      <option value="">—</option>
                      <option value="Embroidery">Embroidery</option>
                      <option value="DTF Print">DTF Print</option>
                      <option value="Screen Print">Screen Print</option>
                      <option value="UV Print">UV Print</option>
                      <option value="Laser Engraving">Laser Engraving</option>
                      <option value="Plain (no customization)">Plain (no customization)</option>
                    </select></div>
                  <div className="col-span-2 md:col-span-2"><div className="label-tiny mb-1">Unit ₹</div>
                    <input type="number" className="input-field" value={it.unit_price ?? 0} onChange={e=>setDraftField(i,"unit_price",Number(e.target.value)||0)} data-testid={`edit-unit-${i}`}/></div>
                  <div className="col-span-2 md:col-span-2"><div className="label-tiny mb-1">Cust ₹/pc</div>
                    <input type="number" className="input-field" value={it.customization_price ?? 0} onChange={e=>setDraftField(i,"customization_price",Number(e.target.value)||0)} data-testid={`edit-cust-${i}`}/></div>
                  <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">3XL+ ₹/pc</div>
                    <input type="number" className="input-field" value={it.oversize_surcharge ?? 0} onChange={e=>setDraftField(i,"oversize_surcharge",Number(e.target.value)||0)} data-testid={`edit-over-${i}`}/></div>
                  <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">GST %</div>
                    <input type="number" className="input-field" value={it.gst_percent ?? 5} onChange={e=>setDraftField(i,"gst_percent",Number(e.target.value)||0)} data-testid={`edit-gst-${i}`}/></div>
                  <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">HSN</div>
                    <input className="input-field" value={it.hsn_code||""} onChange={e=>setDraftField(i,"hsn_code",e.target.value)}/></div>
                  <div className="col-span-2 md:col-span-3"><div className="label-tiny mb-1">Other customization</div>
                    <input className="input-field" placeholder="text description" value={it.other_customization||""} onChange={e=>setDraftField(i,"other_customization",e.target.value)}/></div>
                  <div className="col-span-1 md:col-span-1"><div className="label-tiny mb-1">Other ₹/pc</div>
                    <input type="number" className="input-field" value={it.other_customization_cost ?? 0} onChange={e=>setDraftField(i,"other_customization_cost",Number(e.target.value)||0)}/></div>
                  <div className="col-span-1 md:col-span-1"><div className="label-tiny mb-1">Qty (auto)</div>
                    <div className="input-field bg-white mono">{it.qty}</div></div>
                  <div className="col-span-2 md:col-span-2"><div className="label-tiny mb-1">Line total</div>
                    <div className="input-field bg-white mono">{inr(Number(it.qty)*(Number(it.unit_price||0)+Number(it.customization_price||0)+Number(it.other_customization_cost||0)) + Number(it.oversize_surcharge||0)*OVERSIZE_KEYS.reduce((a,k)=>a+Number((it.sizes||{})[k]||0),0))}</div></div>
                </div>
                <div className="mt-3">
                  <div className="label-tiny mb-1.5">Sizes <span className="normal-case text-[10px]">(auto-sums Qty)</span></div>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                    {SIZES_LIST.map(sz => (
                      <div key={sz}>
                        <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                        <input type="number" min="0" className="input-field text-center px-1"
                          value={(it.sizes||{})[sz] ?? ""} placeholder="—"
                          data-testid={`edit-size-${i}-${sz}`}
                          onChange={e => setDraftSize(i, sz, e.target.value)}/>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {openTee !== -1 && (
          <div className="mt-4 p-3 bg-[#FBF8F1] border border-[#EAE5DB] rounded-md">
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny">Print zones — {o.items[openTee].product}</div>
              <div className="flex gap-1.5">
                <button className="btn-ghost text-xs" onClick={() => setOpenTee(-1)}>Cancel</button>
                <button className="btn-primary text-xs" onClick={() => saveZones(openTee)} data-testid="overview-tee-save">Save</button>
              </div>
            </div>
            <TShirtDiagram
              value={teeDraft[openTee] ?? normalisePL(o.items[openTee].print_locations)}
              onChange={(v) => setTeeDraft(d => ({...d, [openTee]: v}))}
            />
          </div>
        )}

        {o.items.some(i => i.sizes && Object.keys(i.sizes).length) && (
          <div className="mt-5">
            <div className="label-tiny mb-2">Size breakup</div>
            {o.items.map(it => it.sizes && Object.keys(it.sizes).length > 0 && (
              <div key={it.id} className="mb-2">
                <div className="text-sm font-medium mb-1">{it.product}</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(it.sizes).map(([sz, n]) => (
                    <span key={sz} className="badge badge-neutral">{sz}: <b className="ml-1">{n}</b></span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-4">
        <ArtworkCard o={o} reload={reload} />
        <div className="card-surface p-5">
          <div className="label-tiny mb-2">Advance & balance</div>
          <div className="space-y-2 text-sm">
            <div><div className="text-xs text-[#757169] mb-1">Advance received (₹)</div>
              <input type="number" className="input-field" value={adv} onChange={e=>setAdv(Number(e.target.value))} data-testid="advance-input"/></div>
            <div className="flex justify-between pt-2"><span>Balance</span><span className={`mono font-bold ${balance>0?"text-[#B83A3A]":"text-[#4A7258]"}`}>{inr(balance)}</span></div>
            <div><div className="text-xs text-[#757169] mb-1">Expected delivery</div>
              <input type="date" className="input-field" value={exp} onChange={e=>setExp(e.target.value)} data-testid="delivery-date"/></div>
            <button onClick={savePayments} className="btn-primary w-full mt-1" data-testid="save-order-payments">Save</button>
          </div>
        </div>
        <div className="card-surface p-5">
          <div className="label-tiny mb-2">Financial</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="mono">{inr(o.subtotal)}</span></div>
            <div className="flex justify-between"><span>GST ({o.gst_percent}%)</span><span className="mono">{inr(o.gst_amount)}</span></div>
            <div className="flex justify-between font-display font-bold pt-2 border-t border-[#EAE5DB]"><span>Total</span><span className="mono">{inr(o.total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
const TL = ({ label, at, done }) => (
  <div className="flex items-start gap-3">
    <div className={`w-2 h-2 rounded-full mt-1.5 ${done?"bg-[#4A7258]":"bg-[#DED8CB]"}`} />
    <div className="flex-1">
      <div className={done?"text-[#2C2B29]":"text-[#757169]"}>{label}</div>
      {at && <div className="text-xs text-[#757169] mono">{at.slice(0,16).replace("T"," ")}</div>}
    </div>
  </div>
);

// ---------------- Production ----------------
function ProductionTab({ o, vendors, reload }) {
  const totalPcs = (o.items || []).reduce((a,b)=>a + Number(b.qty||0), 0);
  const orderSizes = (() => {
    const merged = {};
    (o.items || []).forEach(it => {
      Object.entries(it.sizes || {}).forEach(([sz, n]) => { merged[sz] = (merged[sz] || 0) + Number(n || 0); });
    });
    return merged;
  })();
  const materialBits = (o.items || []).map(it => `${it.product||""}${it.color?" "+it.color:""}`).filter(Boolean).join(" · ");
  const orderExpectedAt = o.expected_delivery_at ? o.expected_delivery_at.slice(0,10) : "";

  // Auto-prefill PO from order (material, qty, sizes, expected_at)
  const [po, setPo] = useState({ vendor_name: "", po_no: "", material: materialBits, qty: totalPcs, amount: 0, expected_at: orderExpectedAt, sizes: { ...orderSizes } });
  const [cust, setCust] = useState({ type: "embroidery", vendor: "", cost: 0, qty_sent: totalPcs, notes: "", attachment_url: "", attachment_note: "", print_colors: "", sizes_sent: { ...orderSizes } });
  const [editCust, setEditCust] = useState(null);

  // Bulk line-item builder — one row per order item, so multiple POs / customization
  // jobs can be procured / sent / received back with independent qty, sizes and cost.
  const buildBulkRows = () => (o.items || []).map(it => ({
    order_item_id: it.id,
    included: true,
    label: `${it.product || ""}${it.color ? " · " + it.color : ""}`.trim() || "(item)",
    material: `${it.product || ""}${it.color ? " " + it.color : ""}`.trim(),
    qty: Number(it.qty) || 0,
    amount: 0,
    cost: 0,
    sizes: { ...(it.sizes || {}) },
  }));
  const [bulkPo, setBulkPo] = useState({ vendor_name: "", po_no: "", expected_at: orderExpectedAt, rows: buildBulkRows() });
  const [bulkCust, setBulkCust] = useState({ vendor: "", type: "embroidery", notes: "", attachment_url: "", attachment_note: "", print_colors: "", rows: buildBulkRows() });
  const [bulkPoBusy, setBulkPoBusy] = useState(false);
  const [bulkCustBusy, setBulkCustBusy] = useState(false);
  const [showBulkPo, setShowBulkPo] = useState(false);
  const [showBulkCust, setShowBulkCust] = useState(false);

  useEffect(() => {
    setBulkPo(b => ({ ...b, expected_at: b.expected_at || orderExpectedAt, rows: b.rows.length === (o.items || []).length ? b.rows : buildBulkRows() }));
    setBulkCust(b => ({ ...b, rows: b.rows.length === (o.items || []).length ? b.rows : buildBulkRows() }));
  }, [o.id, orderExpectedAt, (o.items || []).length]);

  // If order data loads later, keep prefill state fresh (only when user hasn't started typing)
  useEffect(() => {
    setPo(p => (p.vendor_name || p.po_no ? p : { ...p, material: p.material || materialBits, qty: p.qty || totalPcs, expected_at: p.expected_at || orderExpectedAt, sizes: Object.keys(p.sizes||{}).length ? p.sizes : { ...orderSizes } }));
    setCust(c => (c.vendor ? c : { ...c, qty_sent: c.qty_sent || totalPcs, sizes_sent: Object.keys(c.sizes_sent||{}).length ? c.sizes_sent : { ...orderSizes } }));
  }, [o.id, totalPcs, materialBits, orderExpectedAt]);

  const vendorOpts = vendors.map(v => ({ value: v.name, label: v.name, meta: v }));

  const setPoSize = (sz, n) => setPo(p => {
    const sizes = { ...(p.sizes || {}) };
    if (!n) delete sizes[sz]; else sizes[sz] = Number(n) || 0;
    const total = Object.values(sizes).reduce((a,b)=>a+Number(b||0),0);
    return { ...p, sizes, qty: total || p.qty };
  });
  const setCustSize = (sz, n) => setCust(c => {
    const sizes_sent = { ...(c.sizes_sent || {}) };
    if (!n) delete sizes_sent[sz]; else sizes_sent[sz] = Number(n) || 0;
    const total = Object.values(sizes_sent).reduce((a,b)=>a+Number(b||0),0);
    return { ...c, sizes_sent, qty_sent: total || c.qty_sent };
  });

  const setBulkRow = (kind, idx, patch) => {
    if (kind === "po") setBulkPo(b => ({ ...b, rows: b.rows.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
    else setBulkCust(b => ({ ...b, rows: b.rows.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  };
  const setBulkRowSize = (kind, idx, sz, raw) => {
    const v = raw === "" ? 0 : Number(raw) || 0;
    const state = kind === "po" ? bulkPo : bulkCust;
    const row = state.rows[idx];
    const sizes = { ...(row.sizes || {}) };
    if (!v) delete sizes[sz]; else sizes[sz] = v;
    const total = Object.values(sizes).reduce((a, b) => a + Number(b || 0), 0);
    setBulkRow(kind, idx, { sizes, qty: total || row.qty });
  };

  const submitBulkPo = async (e) => {
    e.preventDefault();
    if (!bulkPo.vendor_name) return toast.error("Pick a vendor");
    const active = bulkPo.rows.filter(r => r.included && r.qty > 0);
    if (!active.length) return toast.error("Include at least one line");
    setBulkPoBusy(true);
    try {
      for (const [idx, r] of active.entries()) {
        const suffix = active.length > 1 ? `/${idx + 1}` : "";
        await api.post(`/orders/${o.id}/procurements`, {
          vendor_name: bulkPo.vendor_name,
          po_no: bulkPo.po_no ? `${bulkPo.po_no}${suffix}` : "",
          material: r.material,
          qty: Number(r.qty) || 0,
          amount: Number(r.amount) || 0,
          expected_at: bulkPo.expected_at || "",
          sizes: r.sizes || {},
        });
      }
      toast.success(`${active.length} PO${active.length > 1 ? "s" : ""} created`);
      setBulkPo({ vendor_name: "", po_no: "", expected_at: orderExpectedAt, rows: buildBulkRows() });
      setShowBulkPo(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBulkPoBusy(false);
  };
  const submitBulkCust = async (e) => {
    e.preventDefault();
    if (!bulkCust.vendor) return toast.error("Pick a customization unit");
    const active = bulkCust.rows.filter(r => r.included && r.qty > 0);
    if (!active.length) return toast.error("Include at least one line");
    setBulkCustBusy(true);
    try {
      for (const r of active) {
        await api.post(`/orders/${o.id}/customizations`, {
          vendor: bulkCust.vendor,
          type: bulkCust.type,
          cost: Number(r.cost) || 0,
          qty_sent: Number(r.qty) || 0,
          notes: bulkCust.notes ? `${bulkCust.notes} · ${r.label}` : r.label,
          attachment_url: bulkCust.attachment_url,
          attachment_note: bulkCust.attachment_note,
          print_colors: bulkCust.print_colors,
          sizes_sent: r.sizes || {},
        });
      }
      toast.success(`${active.length} customization job${active.length > 1 ? "s" : ""} sent`);
      setBulkCust({ vendor: "", type: "embroidery", notes: "", attachment_url: "", attachment_note: "", print_colors: "", rows: buildBulkRows() });
      setShowBulkCust(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBulkCustBusy(false);
  };

  const addPO = async (e) => {
    e.preventDefault();
    try { await api.post(`/orders/${o.id}/procurements`, po); reload();
      setPo({ vendor_name: "", po_no: "", material: materialBits, qty: totalPcs, amount: 0, expected_at: orderExpectedAt, sizes: { ...orderSizes } });
      toast.success("PO added");
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };
  const receivePO = async (pid) => { await api.post(`/orders/${o.id}/procurements/${pid}/receive`); reload(); toast.success("Material received"); };
  const addCust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/orders/${o.id}/customizations`, { ...cust, qty_sent: Number(cust.qty_sent) || totalPcs });
      reload();
      setCust({ type: "embroidery", vendor: "", cost: 0, qty_sent: totalPcs, notes: "", attachment_url: "", attachment_note: "", print_colors: "", sizes_sent: { ...orderSizes } });
      toast.success("Sent to customization");
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveCustQty = async (cid, patch) => {
    try {
      await api.put(`/orders/${o.id}/customizations/${cid}`, patch);
      toast.success("Job updated"); setEditCust(null); reload();
    } catch { toast.error("Failed"); }
  };
  const uploadCustFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setCust(c => ({ ...c, attachment_url: reader.result, attachment_note: file.name }));
    reader.readAsDataURL(file);
  };
  const completeCust = async (cid) => { await api.post(`/orders/${o.id}/customizations/${cid}/complete`); reload(); toast.success("Customization done"); };
  const SIZES = ["XS","S","M","L","XL","2XL","3XL","4XL"];

  return (
    <div className="space-y-5">
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-display font-semibold">Procurement</h3>
          {(o.items || []).length > 0 && (
            <button type="button" onClick={() => setShowBulkPo(v => !v)} className="btn-ghost text-xs" data-testid="bulk-po-toggle">
              {showBulkPo ? "Hide bulk PO from order" : `+ Bulk PO from ${o.items.length} order item${o.items.length>1?"s":""}`}
            </button>
          )}
        </div>

        {/* Bulk PO — one PO row per order line item, prefilled + editable */}
        {showBulkPo && (
          <form onSubmit={submitBulkPo} className="mb-4 p-3 border border-dashed border-[#C45A41] rounded-md bg-[#FBF4EE] space-y-3" data-testid="bulk-po-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="label-tiny mb-1">Vendor *</div>
                <PickerWithAdd testid="bulk-po-vendor" value={bulkPo.vendor_name}
                  requirePhone
                  onChange={v => setBulkPo({ ...bulkPo, vendor_name: v })}
                  options={vendorOpts} placeholder="Select vendor"
                  addLabel="vendor"
                  onCreate={async (name, phone) => { const opt = await createVendor(name, phone); return opt; }} />
              </div>
              <div>
                <div className="label-tiny mb-1">PO # prefix <span className="normal-case text-[10px]">(each line gets /1, /2…)</span></div>
                <input className="input-field" value={bulkPo.po_no} onChange={e => setBulkPo({ ...bulkPo, po_no: e.target.value })} placeholder="Optional" data-testid="bulk-po-no"/>
              </div>
              <div>
                <div className="label-tiny mb-1">Expected by</div>
                <input type="date" className="input-field" value={bulkPo.expected_at} onChange={e => setBulkPo({ ...bulkPo, expected_at: e.target.value })}/>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead><tr><th style={{width:32}}></th><th>Item / Material</th><th style={{width:80}}>Qty</th><th>Sizes</th><th style={{width:110}}>Amount ₹</th></tr></thead>
                <tbody>
                  {bulkPo.rows.map((r, idx) => (
                    <tr key={idx} className={!r.included ? "opacity-50" : ""} data-testid={`bulk-po-row-${idx}`}>
                      <td><input type="checkbox" checked={r.included} onChange={e => setBulkRow("po", idx, { included: e.target.checked })} data-testid={`bulk-po-inc-${idx}`}/></td>
                      <td>
                        <div className="text-xs font-medium">{r.label}</div>
                        <input className="input-field mt-1 text-xs" value={r.material} onChange={e => setBulkRow("po", idx, { material: e.target.value })} placeholder="Material description"/>
                      </td>
                      <td><input type="number" className="input-field text-xs" value={r.qty || ""} onChange={e => setBulkRow("po", idx, { qty: Number(e.target.value) || 0 })} data-testid={`bulk-po-qty-${idx}`}/></td>
                      <td>
                        <div className="grid grid-cols-4 gap-1">
                          {SIZES.map(sz => (
                            <div key={sz}>
                              <div className="text-[9px] text-center text-[#757169]">{sz}</div>
                              <input type="number" min="0" className="input-field text-xs text-center px-1"
                                value={r.sizes?.[sz] ?? ""} placeholder="—"
                                onChange={e => setBulkRowSize("po", idx, sz, e.target.value)}
                                data-testid={`bulk-po-size-${idx}-${sz}`}/>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td><input type="number" className="input-field text-xs" value={r.amount || ""} onChange={e => setBulkRow("po", idx, { amount: Number(e.target.value) || 0 })} data-testid={`bulk-po-amount-${idx}`}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowBulkPo(false)} className="btn-ghost text-xs">Cancel</button>
              <button type="submit" disabled={bulkPoBusy} className="btn-primary text-xs" data-testid="bulk-po-submit">{bulkPoBusy ? "Creating…" : `Create ${bulkPo.rows.filter(r=>r.included && r.qty>0).length} PO(s)`}</button>
            </div>
          </form>
        )}

        <form onSubmit={addPO} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <div className="label-tiny mb-1">Vendor</div>
            <PickerWithAdd testid="po-vendor" value={po.vendor_name}
              requirePhone
              onChange={(v)=>setPo({...po, vendor_name: v})}
              options={vendorOpts} placeholder="Select vendor"
              addLabel="vendor"
              onCreate={async (name, phone) => { const opt = await createVendor(name, phone); return opt; }} />
          </div>
          <div><div className="label-tiny mb-1">PO number</div><input className="input-field" value={po.po_no} onChange={e=>setPo({...po,po_no:e.target.value})} placeholder="Auto if blank"/></div>
          <div><div className="label-tiny mb-1">Material</div><input className="input-field" value={po.material} onChange={e=>setPo({...po,material:e.target.value})} placeholder="e.g. Polo blanks 220gsm"/></div>
          <div><div className="label-tiny mb-1">Qty</div><input type="number" className="input-field" value={po.qty} onChange={e=>setPo({...po,qty:Number(e.target.value)})}/></div>
          <div><div className="label-tiny mb-1">Amount</div><input type="number" className="input-field" value={po.amount} onChange={e=>setPo({...po,amount:Number(e.target.value)})}/></div>
          <div><div className="label-tiny mb-1">Expected by</div><input type="date" className="input-field" value={po.expected_at} onChange={e=>setPo({...po,expected_at:e.target.value})}/></div>
          <div className="md:col-span-3">
            <div className="label-tiny mb-1">Size-wise breakup <span className="normal-case text-[10px]">(auto-summed into Qty · pre-filled from order)</span></div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
              {SIZES.map(sz => (
                <div key={sz}>
                  <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                  <input type="number" min="0" className="input-field text-center px-1"
                    value={po.sizes?.[sz] ?? ""} placeholder="—"
                    data-testid={`po-size-${sz}`}
                    onChange={e => setPoSize(sz, e.target.value === "" ? 0 : Number(e.target.value))} />
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" className="btn-primary" data-testid="po-add" disabled={!po.vendor_name}>Add PO</button>
          </div>
        </form>
        {o.procurements?.length ? (
          <ProcurementGroups
            orderNo={o.order_no}
            orderId={o.id}
            expectedAt={o.expected_delivery_at}
            procurements={o.procurements}
            vendors={vendors}
            onReceive={receivePO}
          />
        ) : <div className="text-sm text-[#757169]">No purchase orders yet.</div>}
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-display font-semibold">Customization jobs</h3>
          {(o.items || []).length > 0 && (
            <button type="button" onClick={() => setShowBulkCust(v => !v)} className="btn-ghost text-xs" data-testid="bulk-cust-toggle">
              {showBulkCust ? "Hide bulk send from order" : `+ Bulk send from ${o.items.length} order item${o.items.length>1?"s":""}`}
            </button>
          )}
        </div>

        {/* Bulk customization — send multiple line-items to same vendor in one go */}
        {showBulkCust && (
          <form onSubmit={submitBulkCust} className="mb-4 p-3 border border-dashed border-[#C45A41] rounded-md bg-[#FBF4EE] space-y-3" data-testid="bulk-cust-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <div className="label-tiny mb-1">Vendor / unit *</div>
                <PickerWithAdd testid="bulk-cust-vendor" value={bulkCust.vendor}
                  requirePhone
                  onChange={v => setBulkCust({ ...bulkCust, vendor: v })}
                  options={vendorOpts} placeholder="Select unit"
                  addLabel="vendor"
                  onCreate={async (name, phone) => { const opt = await createVendor(name, phone); return opt; }} />
              </div>
              <div>
                <div className="label-tiny mb-1">Type</div>
                <select className="input-field" value={bulkCust.type} onChange={e => setBulkCust({ ...bulkCust, type: e.target.value })} data-testid="bulk-cust-type">
                  <option value="embroidery">Embroidery</option>
                  <option value="dtf">DTF print</option>
                  <option value="screen">Screen print</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="label-tiny mb-1">Common notes to vendor</div>
                <input className="input-field" value={bulkCust.notes} onChange={e => setBulkCust({ ...bulkCust, notes: e.target.value })} placeholder="Any common instruction"/>
              </div>
              <div className="md:col-span-4">
                <div className="label-tiny mb-1">Print / thread colors <span className="normal-case text-[10px]">(same for all lines, shows on every job card)</span></div>
                <input className="input-field" value={bulkCust.print_colors || ""} onChange={e => setBulkCust({ ...bulkCust, print_colors: e.target.value })} placeholder='e.g. "Red, White, Gold thread"' data-testid="bulk-cust-print-colors"/>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead><tr><th style={{width:32}}></th><th>Line item</th><th style={{width:80}}>Qty sent</th><th>Sizes sent</th><th style={{width:110}}>Cost ₹</th></tr></thead>
                <tbody>
                  {bulkCust.rows.map((r, idx) => (
                    <tr key={idx} className={!r.included ? "opacity-50" : ""} data-testid={`bulk-cust-row-${idx}`}>
                      <td><input type="checkbox" checked={r.included} onChange={e => setBulkRow("cust", idx, { included: e.target.checked })} data-testid={`bulk-cust-inc-${idx}`}/></td>
                      <td className="text-xs font-medium">{r.label}</td>
                      <td><input type="number" className="input-field text-xs" value={r.qty || ""} onChange={e => setBulkRow("cust", idx, { qty: Number(e.target.value) || 0 })} data-testid={`bulk-cust-qty-${idx}`}/></td>
                      <td>
                        <div className="grid grid-cols-4 gap-1">
                          {SIZES.map(sz => (
                            <div key={sz}>
                              <div className="text-[9px] text-center text-[#757169]">{sz}</div>
                              <input type="number" min="0" className="input-field text-xs text-center px-1"
                                value={r.sizes?.[sz] ?? ""} placeholder="—"
                                onChange={e => setBulkRowSize("cust", idx, sz, e.target.value)}
                                data-testid={`bulk-cust-size-${idx}-${sz}`}/>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td><input type="number" className="input-field text-xs" value={r.cost || ""} onChange={e => setBulkRow("cust", idx, { cost: Number(e.target.value) || 0 })} data-testid={`bulk-cust-cost-${idx}`}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowBulkCust(false)} className="btn-ghost text-xs">Cancel</button>
              <button type="submit" disabled={bulkCustBusy} className="btn-primary text-xs" data-testid="bulk-cust-submit">{bulkCustBusy ? "Sending…" : `Send ${bulkCust.rows.filter(r=>r.included && r.qty>0).length} job(s)`}</button>
            </div>
          </form>
        )}

        <form onSubmit={addCust} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div><div className="label-tiny mb-1">Type</div>
            <select className="input-field" value={cust.type} onChange={e=>setCust({...cust,type:e.target.value})}>
              <option value="embroidery">Embroidery</option><option value="dtf">DTF Print</option>
              <option value="screen">Screen Print</option><option value="uv">UV Print</option><option value="laser">Laser Engraving</option>
            </select>
          </div>
          <div><div className="label-tiny mb-1">Unit / Vendor</div>
            <PickerWithAdd testid="cust-vendor" value={cust.vendor}
              requirePhone
              onChange={v=>setCust({...cust, vendor: v})}
              options={vendorOpts} placeholder="Select unit"
              addLabel="vendor"
              onCreate={async (name, phone)=>{ const opt = await createVendor(name, phone); return opt; }} />
          </div>
          <div><div className="label-tiny mb-1">Cost</div><input type="number" className="input-field" value={cust.cost} onChange={e=>setCust({...cust,cost:Number(e.target.value)})}/></div>
          <div><div className="label-tiny mb-1">Qty sent to unit</div><input type="number" className="input-field" placeholder={String(totalPcs)} value={cust.qty_sent||""} onChange={e=>setCust({...cust,qty_sent:Number(e.target.value)||0})} data-testid="cust-qty-sent"/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Notes</div><input className="input-field" value={cust.notes} onChange={e=>setCust({...cust,notes:e.target.value})}/></div>
          <div className="md:col-span-4">
            <div className="label-tiny mb-1">Size-wise sent to vendor <span className="normal-case text-[10px]">(auto-summed into Qty · pre-filled from order)</span></div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
              {SIZES.map(sz => (
                <div key={sz}>
                  <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                  <input type="number" min="0" className="input-field text-center px-1"
                    value={cust.sizes_sent?.[sz] ?? ""} placeholder="—"
                    data-testid={`cust-size-sent-${sz}`}
                    onChange={e => setCustSize(sz, e.target.value === "" ? 0 : Number(e.target.value))} />
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Print / thread colors <span className="normal-case text-[10px]">(shows on job card)</span></div>
            <input className="input-field" value={cust.print_colors || ""} onChange={e=>setCust({...cust,print_colors:e.target.value})} placeholder="e.g. Red, Black, Gold thread" data-testid="cust-print-colors"/>
          </div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Attachment / artwork file</div>
            <input type="file" accept="image/*,.pdf,.ai,.eps,.psd" className="input-field text-xs file:mr-2 file:rounded file:border-0 file:bg-[#242321] file:text-white file:px-2 file:py-1"
              onChange={e=>uploadCustFile(e.target.files?.[0])} data-testid="cust-attachment" />
            {cust.attachment_url && <div className="text-[10px] text-[#4A7258] mt-1">✓ {cust.attachment_note || "file attached"}</div>}
          </div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Or paste artwork URL</div>
            <input className="input-field" value={cust.attachment_url && !cust.attachment_url.startsWith("data:") ? cust.attachment_url : ""} placeholder="https://…"
              onChange={e=>setCust({...cust, attachment_url: e.target.value})} data-testid="cust-attachment-url"/>
          </div>
          <div className="md:col-span-4 flex justify-end">
            <button className="btn-primary" data-testid="cust-add">Send to customization</button>
          </div>
        </form>
        {o.customizations?.length ? (
          <>
            <CustomizationGroupsSummary customizations={o.customizations} vendors={vendors} />
            <table className="data-table">
            <thead><tr><th>Type</th><th>Vendor</th><th>Sent</th><th>Rec.</th><th>Damaged</th><th>Cost</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {o.customizations.map(c => {
                const jobUrl = `${window.location.origin}/print/job-card/${o.id}?job=${c.id}`;
                const orderTotalPcs = o.items.reduce((a,b)=>a+Number(b.qty||0),0);
                const deliv = o.expected_delivery_at ? `\nDeliver by: ${o.expected_delivery_at.slice(0,10)}` : "";
                const sentQty = c.qty_sent || orderTotalPcs;
                const waMsg = `Job Card ${o.order_no} — ${c.type.toUpperCase()}\n${sentQty} pcs${deliv}\n\nOpen: ${jobUrl}${c.attachment_url && !c.attachment_url.startsWith("data:") ? `\nArtwork: ${c.attachment_url}` : ""}`;
                const vend = vendors.find(v => v.name === c.vendor);
                const damaged = Number(c.qty_damaged || 0) > 0;
                const rowCls = damaged ? "bg-[#FBEBE7]" : "";
                const isEditing = editCust?.cid === c.id;
                return (
                  <React.Fragment key={c.id}>
                  <tr className={rowCls}>
                    <td className="capitalize">{c.type}</td>
                    <td>{c.vendor
                      ? <Link to={`/vendors/${vend?.id || ""}`} className="hover:underline" onClick={ev => { if (!vend?.id) ev.preventDefault(); }}>{c.vendor}</Link>
                      : "—"}</td>
                    <td className="mono">{c.qty_sent || "—"}</td>
                    <td className="mono">{c.qty_received || "—"}</td>
                    <td className={`mono ${damaged?"text-[#B83A3A] font-bold":""}`}>{c.qty_damaged || "—"}</td>
                    <td className="mono">{inr(c.cost)}</td>
                    <td>
                      {damaged && <span className="badge badge-danger mr-1" title={c.damage_notes||""}>⚠ Damage</span>}
                      {c.completed_at
                        ? <span className="badge badge-success">Done</span>
                        : <button className="btn-ghost text-xs" onClick={()=>completeCust(c.id)} data-testid={`cust-complete-${c.id}`}>Mark done</button>}
                    </td>
                    <td>
                      <div className="flex gap-1.5 flex-wrap items-center">
                        <a href={jobUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1" title="Preview Job Card (unit-scoped, no prices)"><Printer size={11}/>Preview</a>
                        {c.attachment_url && (
                          <AttachmentLink url={c.attachment_url} name={c.attachment_note || `artwork-${(c.type||"").toLowerCase()}.png`} className="btn-ghost text-xs">Artwork</AttachmentLink>
                        )}
                        <button className="btn-ghost text-xs" onClick={()=>setEditCust(isEditing ? null : {
                          cid: c.id,
                          qty_received: c.qty_received||sentQty,
                          qty_damaged: c.qty_damaged||0,
                          damage_notes: c.damage_notes||"",
                          sizes_received: { ...(c.sizes_received || c.sizes_sent || {}) },
                          sizes_damaged: { ...(c.sizes_damaged || {}) },
                          sizes_sent_ref: c.sizes_sent || {},
                          // full-record edit fields
                          vendor: c.vendor || "",
                          type: c.type || "embroidery",
                          cost: c.cost || 0,
                          notes: c.notes || "",
                          print_colors: c.print_colors || "",
                          full: false,
                        })} data-testid={`cust-log-recv-${c.id}`}>
                          {isEditing ? "Cancel" : "Log received"}
                        </button>
                        <button className="btn-ghost text-xs" onClick={()=>setEditCust({
                          cid: c.id,
                          qty_received: c.qty_received||sentQty,
                          qty_damaged: c.qty_damaged||0,
                          damage_notes: c.damage_notes||"",
                          sizes_received: { ...(c.sizes_received || c.sizes_sent || {}) },
                          sizes_damaged: { ...(c.sizes_damaged || {}) },
                          sizes_sent_ref: c.sizes_sent || {},
                          vendor: c.vendor || "",
                          type: c.type || "embroidery",
                          cost: c.cost || 0,
                          notes: c.notes || "",
                          print_colors: c.print_colors || "",
                          full: true,
                        })} data-testid={`cust-edit-${c.id}`}>Edit</button>
                        <button className="btn-ghost text-xs text-[#B83A3A]" onClick={async ()=>{
                          if (!window.confirm(`Delete this ${c.type} job? This is not reversible.`)) return;
                          try { await api.delete(`/orders/${o.id}/customizations/${c.id}`); toast.success("Deleted"); reload(); }
                          catch { toast.error("Failed"); }
                        }} data-testid={`cust-delete-${c.id}`}>Delete</button>
                        {vend?.phone
                          ? <WhatsAppButton phone={vend.phone} message={waMsg} label="Send" size="sm" testid={`wa-jobcard-${c.id}`}/>
                          : <span className="text-[10px] text-[#75706A] italic">Add phone to vendor</span>}
                      </div>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="bg-[#FBF8F1]">
                      <td colSpan={8}>
                        <div className="p-3 space-y-3">
                          {editCust.full && (
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end pb-3 border-b border-[#EAE5DB]" data-testid={`cust-full-edit-${c.id}`}>
                              <div><div className="label-tiny mb-1">Type</div>
                                <select className="input-field" value={editCust.type} onChange={e=>setEditCust({...editCust, type: e.target.value})} data-testid={`cust-edit-type-${c.id}`}>
                                  <option value="embroidery">Embroidery</option>
                                  <option value="dtf">DTF print</option>
                                  <option value="screen">Screen print</option>
                                  <option value="uv">UV print</option>
                                  <option value="laser">Laser engraving</option>
                                </select></div>
                              <div><div className="label-tiny mb-1">Vendor / unit</div>
                                <input className="input-field" value={editCust.vendor} onChange={e=>setEditCust({...editCust, vendor: e.target.value})}/></div>
                              <div><div className="label-tiny mb-1">Cost ₹</div>
                                <input type="number" className="input-field" value={editCust.cost||""} onChange={e=>setEditCust({...editCust, cost: Number(e.target.value)||0})}/></div>
                              <div className="md:col-span-2"><div className="label-tiny mb-1">Print / thread colors</div>
                                <input className="input-field" value={editCust.print_colors} onChange={e=>setEditCust({...editCust, print_colors: e.target.value})} placeholder="e.g. Red, Black, Gold thread"/></div>
                              <div className="md:col-span-5"><div className="label-tiny mb-1">Notes</div>
                                <input className="input-field" value={editCust.notes} onChange={e=>setEditCust({...editCust, notes: e.target.value})}/></div>
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                            <div><div className="label-tiny mb-1">Qty received back</div>
                              <input type="number" className="input-field" value={editCust.qty_received} onChange={e=>setEditCust({...editCust, qty_received: Number(e.target.value)||0})} data-testid={`cust-qty-rec-${c.id}`}/></div>
                            <div><div className="label-tiny mb-1">Qty damaged / rejects</div>
                              <input type="number" className="input-field" value={editCust.qty_damaged} onChange={e=>setEditCust({...editCust, qty_damaged: Number(e.target.value)||0})} data-testid={`cust-qty-dmg-${c.id}`}/></div>
                            <div className="md:col-span-2"><div className="label-tiny mb-1">Damage notes</div>
                              <input className="input-field" placeholder="What went wrong? Which vendor is at fault?" value={editCust.damage_notes} onChange={e=>setEditCust({...editCust, damage_notes: e.target.value})}/></div>
                          </div>
                          <div>
                            <div className="label-tiny mb-1">Size-wise received back <span className="normal-case text-[10px]">(auto-summed)</span></div>
                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                              {SIZES.map(sz => {
                                const sent = editCust.sizes_sent_ref?.[sz];
                                return (
                                  <div key={sz}>
                                    <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}{sent?<span className="text-[9px] text-[#4A7258]"> ({sent})</span>:null}</div>
                                    <input type="number" min="0" className="input-field text-center px-1"
                                      value={editCust.sizes_received?.[sz] ?? ""} placeholder="—"
                                      data-testid={`cust-size-recv-${c.id}-${sz}`}
                                      onChange={e => {
                                        const v = e.target.value === "" ? 0 : Number(e.target.value);
                                        const sizes = { ...(editCust.sizes_received || {}) };
                                        if (!v) delete sizes[sz]; else sizes[sz] = v;
                                        const total = Object.values(sizes).reduce((a,b)=>a+Number(b||0),0);
                                        setEditCust({...editCust, sizes_received: sizes, qty_received: total || editCust.qty_received});
                                      }} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <div className="label-tiny mb-1">Size-wise damaged / rejects</div>
                            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                              {SIZES.map(sz => (
                                <div key={sz}>
                                  <div className="text-[10px] text-center text-[#B83A3A] mb-0.5 font-medium">{sz}</div>
                                  <input type="number" min="0" className="input-field text-center px-1"
                                    value={editCust.sizes_damaged?.[sz] ?? ""} placeholder="—"
                                    data-testid={`cust-size-dmg-${c.id}-${sz}`}
                                    onChange={e => {
                                      const v = e.target.value === "" ? 0 : Number(e.target.value);
                                      const sizes = { ...(editCust.sizes_damaged || {}) };
                                      if (!v) delete sizes[sz]; else sizes[sz] = v;
                                      const total = Object.values(sizes).reduce((a,b)=>a+Number(b||0),0);
                                      setEditCust({...editCust, sizes_damaged: sizes, qty_damaged: total || editCust.qty_damaged});
                                    }} />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button className="btn-primary text-xs" onClick={()=>saveCustQty(c.id, {
                              qty_received: editCust.qty_received,
                              qty_damaged: editCust.qty_damaged,
                              damage_notes: editCust.damage_notes,
                              sizes_received: editCust.sizes_received,
                              sizes_damaged: editCust.sizes_damaged,
                              ...(editCust.full ? {
                                type: editCust.type,
                                vendor: editCust.vendor,
                                cost: editCust.cost,
                                notes: editCust.notes,
                                print_colors: editCust.print_colors,
                              } : {}),
                            })} data-testid={`cust-save-recv-${c.id}`}>Save</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </>
        ) : <div className="text-sm text-[#757169]">No jobs yet — send material for customization after it&apos;s received.</div>}
        <p className="text-[10px] text-[#75706A] mt-2 italic">Note: Damage on any customization job automatically highlights the vendor row and appears on their vendor profile.</p>
      </div>
    </div>
  );
}

// ---------------- Dispatch ----------------
function DispatchTab({ o, reload }) {
  const doDispatch = async (patch) => {
    try { await api.post(`/orders/${o.id}/dispatch`, patch); reload(); toast.success("Dispatch updated"); }
    catch(e){ toast.error("Failed"); }
  };
  const feedbackUrl = `${window.location.origin}/rate/${o.rating_token}`;
  const feedbackMsg = `Hi ${o.customer_name}, your order ${o.order_no} from Marsh Clothing has been delivered! 🎉 We'd love your feedback — ${feedbackUrl}`;
  return (
    <div className="card-surface p-5 space-y-4">
      <h3 className="font-display font-semibold">Dispatch & QC</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm p-3 rounded-md border border-[#EAE5DB] cursor-pointer">
          <input type="checkbox" checked={!!o.dispatch?.qc_done} onChange={e=>doDispatch({qc_done:e.target.checked})} data-testid="qc-check"/>
          <span>QC checklist complete</span>
        </label>
        <div>
          <div className="label-tiny mb-1">Courier / Porter</div>
          <input className="input-field" placeholder="Bluedart / Porter driver name" value={o.dispatch?.courier||""} onChange={e=>doDispatch({courier:e.target.value})}/>
        </div>
        <div>
          <div className="label-tiny mb-1">Tracking / Reference</div>
          <input className="input-field" placeholder="Tracking #, LR no., or vehicle plate" value={o.dispatch?.tracking||""} onChange={e=>doDispatch({tracking:e.target.value})}/>
        </div>
        <div className="flex items-end gap-2">
          <button className="btn-ghost text-sm flex-1" onClick={()=>doDispatch({shipped_at: new Date().toISOString()})}>Mark shipped</button>
          <button className="btn-primary text-sm flex-1" onClick={()=>doDispatch({delivered_at: new Date().toISOString()})} data-testid="mark-delivered">Mark delivered</button>
        </div>
      </div>
      {o.dispatch?.delivered_at && (
        <div className="p-4 bg-[#DDEADE] rounded-md">
          <div className="label-tiny mb-2 text-[#2E4A38]">Delivered — send feedback request</div>
          <p className="text-sm text-[#2C2B29] mb-3">4 or 5 stars auto-redirects them to your Google Review page.</p>
          <div className="flex gap-2 flex-wrap">
            {o.customer_phone && <WhatsAppButton phone={o.customer_phone} message={feedbackMsg} label="Send feedback link on WhatsApp" size="lg"/>}
            <button className="btn-ghost text-xs" onClick={()=>{navigator.clipboard.writeText(feedbackUrl); toast.success("Feedback link copied");}}>Copy link</button>
          </div>
        </div>
      )}

      {/* Final product photos before dispatch */}
      <div className="p-4 border border-dashed border-[#E7E1D3] rounded-md">
        <div className="label-tiny mb-2">Final product photos (for records)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {["front","back","sleeve","other"].map(view => (
            <label key={view} className="btn-ghost cursor-pointer text-center py-3">
              <input type="file" hidden accept="image/*" capture="environment" onChange={async e=>{
                const f = e.target.files?.[0]; if (!f) return;
                if (f.size > 3*1024*1024) return toast.error("Max 3 MB");
                const r = new FileReader();
                r.onload = async () => {
                  try { await api.post(`/orders/${o.id}/final-photo`, { view, file_url: r.result, file_name: f.name });
                    toast.success(`${view} photo saved`); reload();
                  } catch(_) { toast.error("Failed"); }
                };
                r.readAsDataURL(f);
              }}/>
              📷 {view.toUpperCase()}
            </label>
          ))}
        </div>
      </div>
      {(o.dispatch?.shipped_at || o.dispatch?.delivered_at) && (
        <div className="text-xs text-[#757169] pt-3 border-t border-[#EAE5DB]">
          {o.dispatch.shipped_at && <>Shipped: <span className="mono">{o.dispatch.shipped_at.slice(0,10)}</span> · </>}
          {o.dispatch.delivered_at && <>Delivered: <span className="mono">{o.dispatch.delivered_at.slice(0,10)}</span></>}
        </div>
      )}
    </div>
  );
}

// ---------------- Expenses & Profit ----------------
function ExpensesTab({ o, expenses, revenue, spent, profit, reload }) {
  const [form, setForm] = useState({ category: "material", description: "", amount: 0, vendor: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/expenses", { ...form, order_id: o.id }); reload();
      setForm({ category: "material", description: "", amount: 0, vendor: "" });
      toast.success("Expense added");
    } catch(e){ toast.error("Failed"); }
  };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Order value" value={inr(revenue)} />
        <Kpi label="Actual spent" value={inr(spent)} tone="text-[#B83A3A]" />
        <Kpi label="Real profit" value={inr(profit)} tone={profit>=0?"text-[#4A7258]":"text-[#B83A3A]"} />
        <Kpi label="Margin" value={revenue > 0 ? `${((profit/revenue)*100).toFixed(1)}%` : "—"} />
      </div>
      <div className="card-surface p-5">
        <h3 className="font-display font-semibold mb-3">Add expense against this order</h3>
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div><div className="label-tiny mb-1">Category</div>
            <select className="input-field" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              <option value="material">Material</option><option value="customization">Customization</option>
              <option value="transport">Transport</option><option value="packaging">Packaging</option><option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Description</div><input className="input-field" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Amount</div><input type="number" step="0.01" required className="input-field" value={form.amount} onChange={e=>setForm({...form,amount:Number(e.target.value)})}/></div>
          <div><div className="label-tiny mb-1">Paid to</div><input className="input-field" value={form.vendor} onChange={e=>setForm({...form,vendor:e.target.value})}/></div>
          <div className="md:col-span-5 flex justify-end"><button className="btn-primary">Record expense</button></div>
        </form>
      </div>
      {expenses.length > 0 && (
        <div className="card-surface overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Paid to</th><th>Amount</th></tr></thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  <td className="mono text-xs">{e.created_at?.slice(0,10)}</td>
                  <td>{e.category}</td>
                  <td>{e.description || "—"}</td>
                  <td>{e.vendor || "—"}</td>
                  <td className="mono">{inr(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
const Kpi = ({label, value, tone}) => (
  <div className="card-surface p-4">
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);

// ---------------- Attachments ----------------
function AttachmentsTab({ orderId, attachments, reload, order }) {
  const [type, setType] = useState("logo");
  const [busy, setBusy] = useState(false);
  const [physicalBusy, setPhysicalBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Max 3 MB per file"); return; }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/attachments", {
          order_id: orderId, type, file_name: file.name, file_url: reader.result,
        });
        toast.success("File uploaded"); reload();
      } catch(e){ toast.error("Failed"); }
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const uploadPhysicalForm = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setPhysicalBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put(`/orders/${orderId}`, { physical_form_url: reader.result, physical_form_name: file.name });
        toast.success("Physical order form saved"); reload();
      } catch { toast.error("Failed"); } finally { setPhysicalBusy(false); }
    };
    reader.readAsDataURL(file);
  };
  const clearPhysicalForm = async () => {
    if (!window.confirm("Remove the physical order form photo?")) return;
    try { await api.put(`/orders/${orderId}`, { physical_form_url: "", physical_form_name: "" }); toast.success("Removed"); reload(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Remove this file?")) return;
    await api.delete(`/attachments/${aid}`); reload(); toast.success("Deleted");
  };

  return (
    <div className="space-y-4">
      {/* Physical order form photo */}
      <div className="card-surface p-5">
        <h3 className="font-display font-semibold mb-3">Physical order form (paper booking)</h3>
        {order?.physical_form_url ? (
          <div className="flex items-start gap-4 flex-wrap">
            <img src={order.physical_form_url} alt="physical order" className="max-h-40 rounded border border-[#EAE5DB]"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{order.physical_form_name || "physical-order.jpg"}</div>
              <div className="flex flex-wrap gap-2 mt-2">
                <AttachmentLink url={order.physical_form_url} name={order.physical_form_name || "physical-order.jpg"} className="btn-ghost text-xs">Open</AttachmentLink>
                <label className="btn-ghost text-xs cursor-pointer">
                  Replace
                  <input type="file" accept="image/*" capture="environment" hidden onChange={e => uploadPhysicalForm(e.target.files?.[0])} data-testid="physical-form-replace"/>
                </label>
                <button className="btn-ghost text-xs text-[#B83A3A]" onClick={clearPhysicalForm}>Remove</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="btn-primary cursor-pointer inline-flex items-center gap-2">
              {physicalBusy ? "Uploading…" : "Take / choose photo"}
              <input type="file" accept="image/*" capture="environment" hidden onChange={e => uploadPhysicalForm(e.target.files?.[0])} data-testid="physical-form-upload"/>
            </label>
            <div className="text-xs text-[#75706A]">Snap a picture of the paper booking so it&apos;s linked to this order.</div>
          </div>
        )}
      </div>

      <div className="card-surface p-5">
        <h3 className="font-display font-semibold mb-3">Upload artwork, mockup or photo</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="label-tiny mb-1">File type</div>
            <select className="input-field" value={type} onChange={e=>setType(e.target.value)}>
              <option value="logo">Customer logo</option>
              <option value="artwork">Artwork / mockup</option>
              <option value="sample_photo">Sample photo</option>
              <option value="final_photo">Final product photo</option>
              <option value="other">Other</option>
            </select>
          </div>
          <label className="btn-primary cursor-pointer inline-flex items-center gap-2">
            {busy ? "Uploading…" : "Choose file"}
            <input type="file" hidden accept="image/*,.pdf,.ai,.eps,.svg" onChange={e=>upload(e.target.files?.[0])} data-testid="attachment-upload"/>
          </label>
          <div className="text-xs text-[#757169]">Max 3 MB. PNG/JPG/PDF/SVG supported.</div>
        </div>
      </div>
      {attachments.length === 0 ? (
        <div className="text-sm text-[#757169] text-center p-8">No files yet. Upload a logo or mockup above.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {attachments.map(a => (
            <div key={a.id} className="card-surface overflow-hidden">
              {a.file_url?.startsWith("data:image") || /\.(png|jpe?g|gif|webp|svg)$/i.test(a.file_name)
                ? <img src={a.file_url} alt={a.file_name} className="w-full h-32 object-cover" />
                : <div className="h-32 bg-[#F6F1E7] flex items-center justify-center text-[#757169] text-xs">{a.file_name}</div>}
              <div className="p-3">
                <div className="text-xs font-medium truncate">{a.file_name}</div>
                <div className="text-[10px] text-[#757169] uppercase tracking-wider mt-0.5">{a.type}</div>
                <div className="mt-2 flex justify-between gap-2">
                  <AttachmentLink url={a.file_url} name={a.file_name} className="btn-ghost text-[10px] flex-1 text-center">View</AttachmentLink>
                  <button onClick={()=>remove(a.id)} className="btn-ghost text-[10px] text-[#B83A3A]">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Procurement grouping ----------------
// Groups POs by their po_no prefix (before "/") OR by (vendor + ordered_at date)
// so bulk-created multi-line POs collapse into one expandable row.
function ProcurementGroups({ procurements, vendors, orderId, orderNo, expectedAt, onReceive }) {
  const [openGroups, setOpenGroups] = React.useState(() => new Set());
  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const groupKey = (p) => {
    const base = (p.po_no || "").split("/")[0].trim();
    if (base) return `po:${base}`;
    return `v:${p.vendor_name || ""}|${(p.ordered_at || "").slice(0, 10)}`;
  };
  const groups = {};
  procurements.forEach(p => {
    const k = groupKey(p);
    if (!groups[k]) groups[k] = { key: k, po_no_base: (p.po_no || "").split("/")[0].trim(), vendor_name: p.vendor_name, ordered_at: p.ordered_at, expected_at: p.expected_at, lines: [] };
    groups[k].lines.push(p);
  });
  const groupList = Object.values(groups).sort((a, b) => (b.ordered_at || "").localeCompare(a.ordered_at || ""));

  const toggle = (key) => setOpenGroups(s => {
    const next = new Set(s); next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  return (
    <div className="space-y-2" data-testid="po-groups">
      {groupList.map(g => {
        const isOpen = openGroups.has(g.key) || g.lines.length === 1;
        const totalQty = g.lines.reduce((a, l) => a + Number(l.qty || 0), 0);
        const totalAmt = g.lines.reduce((a, l) => a + Number(l.amount || 0), 0);
        const allReceived = g.lines.every(l => l.received_at);
        const anyReceived = g.lines.some(l => l.received_at);
        const vend = vendors.find(v => v.name === g.vendor_name);
        const groupUrl = `${window.location.origin}/print/po/${orderId}?po_no=${encodeURIComponent(g.po_no_base || g.key)}`;
        const groupMsg = `Purchase Order ${orderNo}${g.po_no_base ? " · " + g.po_no_base : ""}\n${g.lines.length} lines · ${totalQty} pcs · ${inr(totalAmt)}${expectedAt ? `\nDeliver by: ${expectedAt.slice(0,10)}` : ""}\n\nOpen: ${groupUrl}`;
        return (
          <div key={g.key} className="border border-[#EAE5DB] rounded-md overflow-hidden" data-testid={`po-group-${g.key}`}>
            <button type="button" onClick={() => toggle(g.key)} className="w-full flex items-center justify-between px-3 py-2 bg-[#FBF8F1] hover:bg-[#F6F1E7] text-left" data-testid={`po-group-toggle-${g.key}`}>
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <span className="text-xs">{isOpen ? "▾" : "▸"}</span>
                <span className="font-medium text-sm truncate">
                  {vend?.id
                    ? <Link to={`/vendors/${vend.id}`} className="hover:underline" onClick={e => e.stopPropagation()}>{g.vendor_name || "—"}</Link>
                    : (g.vendor_name || "—")}
                </span>
                {g.po_no_base && <span className="mono text-xs text-[#75706A]">{g.po_no_base}</span>}
                <span className="text-xs text-[#75706A]">{g.lines.length} line{g.lines.length > 1 ? "s" : ""}</span>
                <span className="text-xs">· <span className="mono">{totalQty} pcs</span></span>
                <span className="text-xs">· <span className="mono font-bold">{inr(totalAmt)}</span></span>
                {allReceived ? <span className="badge badge-success">All received</span>
                  : anyReceived ? <span className="badge badge-warn">Partial</span>
                    : <span className="badge">Pending</span>}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                <a href={groupUrl} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1"><Printer size={11}/>PO</a>
                {vend?.phone && <WhatsAppButton phone={vend.phone} message={groupMsg} label="Send" size="sm" testid={`wa-po-group-${g.key}`}/>}
              </div>
            </button>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead><tr><th>PO#</th><th>Material</th><th>Qty</th><th>Amount</th><th>Expected</th><th>Sizes</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {g.lines.map(p => {
                      const isE = editingId === p.id;
                      return (
                      <React.Fragment key={p.id}>
                      <tr data-testid={`po-line-${p.id}`}>
                        <td className="mono">{p.po_no || "—"}</td>
                        <td className="text-xs">{p.material}</td>
                        <td className="mono">{p.qty}</td>
                        <td className="mono">{inr(p.amount)}</td>
                        <td className="mono">{p.expected_at || "—"}</td>
                        <td className="text-[10px]">{Object.entries(p.sizes || {}).map(([s, n]) => `${s}·${n}`).join(" ") || "—"}</td>
                        <td>
                          {p.received_at
                            ? <span className="badge badge-success">Received</span>
                            : <button className="btn-ghost text-xs" onClick={() => onReceive(p.id)} data-testid={`receive-${p.id}`}>Mark received</button>}
                        </td>
                        <td>
                          <div className="flex gap-1 flex-nowrap">
                            <button className="btn-ghost text-xs" onClick={()=>{
                              if (isE) { setEditingId(null); return; }
                              setEditingId(p.id);
                              setDraft({ material: p.material || "", qty: p.qty || 0, amount: p.amount || 0, expected_at: p.expected_at || "", sizes: { ...(p.sizes||{}) } });
                            }} data-testid={`po-edit-${p.id}`}>{isE ? "Cancel" : "Edit"}</button>
                            <button className="btn-ghost text-xs text-[#B83A3A]" onClick={async ()=>{
                              if (!window.confirm("Delete this PO line?")) return;
                              try { await import("@/lib/api").then(m => m.default.delete(`/orders/${orderId}/procurements/${p.id}`)); window.location.reload(); }
                              catch { alert("Failed"); }
                            }} data-testid={`po-delete-${p.id}`}>Delete</button>
                          </div>
                        </td>
                      </tr>
                      {isE && (
                        <tr className="bg-[#FBF8F1]"><td colSpan={8}>
                          <div className="p-2 grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid={`po-edit-panel-${p.id}`}>
                            <div className="md:col-span-2"><div className="label-tiny mb-1">Material</div>
                              <input className="input-field" value={draft.material} onChange={e=>setDraft({...draft, material: e.target.value})}/></div>
                            <div><div className="label-tiny mb-1">Qty</div>
                              <input type="number" className="input-field" value={draft.qty||""} onChange={e=>setDraft({...draft, qty: Number(e.target.value)||0})}/></div>
                            <div><div className="label-tiny mb-1">Amount ₹</div>
                              <input type="number" className="input-field" value={draft.amount||""} onChange={e=>setDraft({...draft, amount: Number(e.target.value)||0})}/></div>
                            <div><div className="label-tiny mb-1">Expected</div>
                              <input type="date" className="input-field" value={draft.expected_at} onChange={e=>setDraft({...draft, expected_at: e.target.value})}/></div>
                            <div className="md:col-span-5">
                              <div className="label-tiny mb-1">Sizes</div>
                              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                                {["XS","S","M","L","XL","2XL","3XL","4XL"].map(sz => (
                                  <div key={sz}>
                                    <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                                    <input type="number" min="0" className="input-field text-center px-1"
                                      value={draft.sizes?.[sz] ?? ""} placeholder="—"
                                      onChange={e => {
                                        const v = e.target.value === "" ? 0 : Number(e.target.value);
                                        const sizes = { ...(draft.sizes || {}) };
                                        if (!v) delete sizes[sz]; else sizes[sz] = v;
                                        const tot = Object.values(sizes).reduce((a,b)=>a+Number(b||0),0);
                                        setDraft({...draft, sizes, qty: tot || draft.qty});
                                      }}/>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="md:col-span-5 flex justify-end">
                              <button className="btn-primary text-xs" onClick={async ()=>{
                                try { await import("@/lib/api").then(m => m.default.put(`/orders/${orderId}/procurements/${p.id}`, draft)); setEditingId(null); window.location.reload(); }
                                catch { alert("Failed"); }
                              }} data-testid={`po-save-${p.id}`}>Save</button>
                            </div>
                          </div>
                        </td></tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// Compact summary strip for customization jobs — groups by vendor + type
function CustomizationGroupsSummary({ customizations, vendors }) {
  const groups = {};
  customizations.forEach(c => {
    const key = `${(c.vendor || "unknown").toLowerCase()}|${(c.type || "").toLowerCase()}`;
    if (!groups[key]) groups[key] = { vendor: c.vendor || "—", type: c.type || "—", sent: 0, received: 0, damaged: 0, cost: 0, count: 0 };
    groups[key].sent += Number(c.qty_sent || 0);
    groups[key].received += Number(c.qty_received || 0);
    groups[key].damaged += Number(c.qty_damaged || 0);
    groups[key].cost += Number(c.cost || 0);
    groups[key].count += 1;
  });
  const list = Object.values(groups).sort((a, b) => b.count - a.count);
  if (list.length <= 1) return null;
  return (
    <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="cust-groups-summary">
      {list.map((g, i) => {
        const vend = vendors.find(v => (v.name || "").toLowerCase() === g.vendor.toLowerCase());
        const dmgPct = g.received ? Math.round((g.damaged / g.received) * 100) : 0;
        return (
          <div key={i} className="p-2.5 rounded-md border border-[#EAE5DB] bg-[#FBF8F1] flex items-center justify-between gap-2 flex-wrap text-xs" data-testid={`cust-group-${i}`}>
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <span className="font-medium truncate">{vend?.id ? <Link to={`/vendors/${vend.id}`} className="hover:underline">{g.vendor}</Link> : g.vendor}</span>
              <span className="capitalize text-[#75706A]">· {g.type}</span>
              <span className="text-[#75706A]">· {g.count} job{g.count > 1 ? "s" : ""}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span>Sent <b className="mono">{g.sent}</b></span>
              <span className="text-[#4A7258]">Rec <b className="mono">{g.received}</b></span>
              {g.damaged > 0 && <span className="text-[#B83A3A]" title={`${dmgPct}% damage rate`}>Dmg <b className="mono">{g.damaged}</b></span>}
              <span className="font-bold">{inr(g.cost)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

